import { Flex, GridAlpha, Heading, ProgressIndicator, useToasts } from '@applyboard/crystal-ui'
import styled from '@emotion/styled'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ApplicationForms,
  ApplicationHeader,
  ApplicationTimeline,
} from '../../components/Application'
import { PolicyBanner } from '../../components/PolicyBanner'
import { PolicyBlockedDialog } from './PolicyBlockedDialog'
import ApplicationStateTag from '../../components/Application/ApplicationStateTag/ApplicationStateTag'
import { Loading } from '../../components/Loading'
import { NavBars } from '../../components/NavBars'
import {
  ApplicationState,
  RawApplicationAggregationResponse,
  RawApplicationResponse,
  TimelineObject,
  useCopyDocuments,
  useCreateApplication,
  useGetApplication,
  useGetApplicationPageProps,
  useGetSpaConfig,
  useGetStudent,
  useListApplications,
  useGetTimelineItems,
  useSubmitApplication,
  useUpdateApplication,
  usePolicyEvaluation,
} from '../../hooks'
import { GetProgramIntakeResponse, useGetProgramIntake } from '../../hooks/useGetProgramIntake'
import { GenericError, hasApplicationFee } from '../../utils'
import { BackToApplication } from './BackToApplication'
import { CreateApplicationErrorBoundary } from './CreateApplicationErrorBoundary'
import { DropdownMenu } from './DropdownMenu'
import { IntakeClosedDialog } from './IntakeClosedDialog'
import { PrefillApplicationDialog } from './PrefillApplicationDialog'
import { SpecificFieldsValidationErrorsDialog } from './SpecificFieldsValidationErrorsDialog/SpecificFieldsValidationErrorsDialog'
import { PayAndSubmitApprovalDialog, SubmitApprovalDialog } from './SubmitApprovalDialog'
import { SubmitConfirmation } from './SubmitConfirmation'
import {
  SpecificFieldValidationErrorField,
  validateRequiredSpecificFields,
} from '../../utils/specificFieldValidations'
import type { UpdateApplicationRequest } from '@backend/students-api/routes/applications/{id}/PATCH/types'
import { GetApplicationPagePropsResponse } from '@backend/students-api/routes/page-props/GET/types'
import { ApplicationSteps } from '../../utils/enums'
import { SelectAlternateProgram } from './SelectAlternateProgram'
import { SelectProgram } from './SelectProgram'
import { StudentApplication } from '../../components/Application/types'

export function CreateApplicationPage() {
  const toast = useToasts()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentApplicationId = searchParams.get('applicationId') ?? ''
  const status = searchParams.get('status') ?? ''
  const { customizeApplicationCreationFlow } = useGetSpaConfig()
  const skipAlternateProgram = customizeApplicationCreationFlow.isSkipAlternateProgram
  const alternateProgramEnabled = !skipAlternateProgram

  const { isLoadingApplication, application, intakePolicyEvaluation, statusInfo } =
    useGetApplication({
      id: currentApplicationId,
    })
  const { isLoadingApplicationPageProps } = useGetApplicationPageProps()
  const { student } = useGetStudent()
  const { intake } = useGetProgramIntake({
    id: application?.attributes?.programSelected?.programIntakeId,
  })
  const { applications } = useListApplications()
  const [openIntakeClosedDialog, setOpenIntakeClosedDialog] = useState(false)
  const { isCreatingApplication, createApplication } = useCreateApplication()
  const { isUpdatingApplication, updateApplication, updateApplicationAsync } = useUpdateApplication(
    {
      id: currentApplicationId,
    },
  )
  const { copyDocuments, isCopyingDocuments, copyDocumentsAsync } = useCopyDocuments({
    targetApplicationId: currentApplicationId,
  })
  const timelineItems = useGetTimelineItems(application)
  const lastTimeline = timelineItems.length
  const { isSubmittingApplication, submitApplication } = useSubmitApplication({
    id: currentApplicationId,
  })
  const [showSpecificFieldsValidationErrorsDialog, setShowSpecificFieldsValidationErrorsDialog] =
    useState(false)
  const [specificFieldsValidationErrors, setSpecificFieldsValidationErrors] = useState<
    SpecificFieldValidationErrorField[]
  >([])

  const [showSubmitApprovalDialog, setShowSubmitApprovalDialog] = useState(false)
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(
    searchParams.get('submitted') === 'true',
  )
  const [showPolicyBlockedDialog, setShowPolicyBlockedDialog] = useState(false)
  const [showPrefillDialog, setShowPrefillDialog] = useState(false)
  const [isPrefilling, setIsPrefilling] = useState(false)

  const previousSubmittedApplication = useMemo(() => {
    if (!applications) return null
    let latest: (typeof applications)[number] | null = null
    let latestSubmittedAt: string | null = null
    for (const app of applications) {
      const submittedAt = app.attributes?.applicationInformation?.submittedAt
      if (app.attributes?.applicationState === ApplicationState.DRAFT || !submittedAt) {
        continue
      }
      if (!latestSubmittedAt || submittedAt.localeCompare(latestSubmittedAt) > 0) {
        latestSubmittedAt = submittedAt
        latest = app
      }
    }
    return latest
  }, [applications])

  const isEditingProgramRef = useRef(false)

  const { policyEvaluation, canSubmitApplication } = usePolicyEvaluation(intakePolicyEvaluation)

  const [currentStep, setCurrentStep] = useState(
    getInitialStep(application, timelineItems, isEditingProgramRef.current, skipAlternateProgram),
  )
  let selectedTab = currentStep - 1

  useEffect(() => {
    if (!isLoadingApplication && application && currentStep === ApplicationSteps.PROGRAM_SELECT) {
      setCurrentStep(
        getInitialStep(
          application,
          timelineItems,
          isEditingProgramRef.current,
          skipAlternateProgram,
        ),
      )
    }
  }, [
    application,
    isLoadingApplication,
    currentStep,
    timelineItems,
    setCurrentStep,
    skipAlternateProgram,
  ])

  useEffect(() => {
    if (
      application?.attributes?.applicationState === ApplicationState.DRAFT &&
      (status === 'error' || status === 'cancelled')
    ) {
      toast.negative(
        new Error(`Your payment could not be processed for application submission. Please make sure you 
           have sufficient funds and your payment details are correct or try an alternative 
           payment method.`),
      )
    } else if (status === 'success') {
      navigate(`/`, { replace: true })
    }
  }, [navigate, status, toast, application?.attributes?.applicationState])

  useEffect(() => {
    if (typeof intake?.attributes?.isAvailable !== 'undefined') {
      setOpenIntakeClosedDialog(!intake.attributes.isAvailable)
    }
  }, [intake])

  useEffect(() => {
    if (
      application &&
      application.attributes?.applicationState !== ApplicationState.DRAFT &&
      !showSubmitConfirmation
    ) {
      navigate(`/applications/${application.id}`, { replace: true })
      toast.warning(
        'This application was previously updated. The page has been refreshed to reflect the current status.',
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application, navigate, showSubmitConfirmation])

  const paySubmitButtonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (showSubmitApprovalDialog === false && paySubmitButtonRef.current) {
      paySubmitButtonRef.current.focus()
    }
  }, [showSubmitApprovalDialog])

  const handleUpdateApplication = (
    primaryIntakeId: string | null,
    alternateIntakeId: string[] | null,
  ) => {
    if (!primaryIntakeId && !alternateIntakeId) {
      toast.negative(new Error('Failed to update program selection.'))
      return
    }

    const isAlternateUpdate = alternateIntakeId !== null
    const isFirstTimeSettingAlternate = !application?.attributes?.alternateProgramSelected

    updateApplication(
      {
        attributes: {
          ...(primaryIntakeId && {
            programSelected: {
              priority: 0,
              programIntakeId: primaryIntakeId,
            },
          }),
          ...(Array.isArray(alternateIntakeId) && {
            alternateProgramSelected: alternateIntakeId.map(id => ({
              programIntakeId: id,
            })),
          }),
        },
      },
      {
        onSuccess: response => {
          queryClient.invalidateQueries({ queryKey: ['intake-search'] })
          queryClient.invalidateQueries({ queryKey: ['applications', response.data.id] })
          setSearchParams({ applicationId: response.data.id })

          if (isAlternateUpdate && isFirstTimeSettingAlternate && previousSubmittedApplication) {
            setShowPrefillDialog(true)
          } else {
            setCurrentStep(ApplicationSteps.PERSONAL_INFORMATION)

            if (!isAlternateUpdate || !isFirstTimeSettingAlternate) {
              toast.positive(
                'Your application has been successfully updated! We recommend that you review the requirements to make sure that you provided all the information needed.',
              )
            }
          }
        },
        onError: err => {
          if (err instanceof Error) {
            toast.negative(new Error(err.message))
            isEditingProgramRef.current = false
          }
        },
      },
    )
  }

  const proceedWithCreateApplication = (intakeId: string) => {
    createApplication(
      { intakeId },
      {
        onSuccess: response => {
          const newApplicationId = response.data.id
          setSearchParams({ applicationId: newApplicationId })
          if (skipAlternateProgram) {
            if (previousSubmittedApplication) {
              // Show dialog once, right before the forms, on the skip-alternate path.
              setShowPrefillDialog(true)
            } else {
              setCurrentStep(ApplicationSteps.PERSONAL_INFORMATION)
            }
          } else {
            setCurrentStep(ApplicationSteps.ALTERNATE_PROGRAM_SELECT)
          }
        },
        onError: err => {
          if (err instanceof Error) {
            toast.negative(new Error(err.message))
          }
        },
      },
    )
  }

  // Always create the application immediately; the prefill dialog is shown later,
  // right before the forms (after this step or after alternate program selection).
  const handleCreateApplication = (intakeId: string) => {
    proceedWithCreateApplication(intakeId)
  }

  const handlePrefillApplication = async () => {
    if (!previousSubmittedApplication) {
      setShowPrefillDialog(false)
      setCurrentStep(ApplicationSteps.PERSONAL_INFORMATION)
      return
    }

    const sourceId = previousSubmittedApplication.id
    const sourceApplication = applications?.find(app => app.id === sourceId)

    if (!sourceApplication?.attributes) {
      setShowPrefillDialog(false)
      setCurrentStep(ApplicationSteps.PERSONAL_INFORMATION)
      toast.negative(
        new Error(
          'No existing application to copy from. Please fill in your application manually.',
        ),
      )
      return
    }

    setIsPrefilling(true)
    try {
      const sourceAttrs = sourceApplication.attributes

      // Strip education history file IDs (transcripts/certificates) — copy-documents handles these.
      const strippedEducationHistory = sourceAttrs.educationHistory
        ? Object.fromEntries(
            Object.entries(sourceAttrs.educationHistory).map(([key, entry]) => {
              const {
                transcripts: _t,
                certificates: _c,
                ...rest
              } = entry as Record<string, unknown>
              return [key, rest]
            }),
          )
        : undefined

      // Strip languageProficiencyId entries — those are uploaded docs, handled by copy-documents.
      // Entries with test scores (no languageProficiencyId) are safe to copy.
      const strippedLanguageProficiencyData = sourceAttrs.languageProficiency
        ?.languageProficiencyData
        ? Object.fromEntries(
            Object.entries(sourceAttrs.languageProficiency.languageProficiencyData).filter(
              ([, entry]) => !entry || !('languageProficiencyId' in (entry as object)),
            ),
          )
        : undefined

      // Strip passport entries that are document references — handled by copy-documents.
      const strippedPassports = sourceAttrs.statusAndCitizenship?.passports
        ? Object.fromEntries(
            Object.entries(sourceAttrs.statusAndCitizenship.passports).filter(
              ([, entry]) => !entry || !('passportId' in (entry as object)),
            ),
          )
        : undefined

      const attributesToCopy: UpdateApplicationRequest['data']['attributes'] = {
        ...(sourceAttrs.personalInformation != null && {
          personalInformation: sourceAttrs.personalInformation,
        }),
        ...(strippedEducationHistory != null && {
          educationHistory: strippedEducationHistory,
        }),
        ...(sourceAttrs.languageProficiency != null && {
          languageProficiency: {
            ...sourceAttrs.languageProficiency,
            ...(strippedLanguageProficiencyData != null && {
              languageProficiencyData: strippedLanguageProficiencyData,
            }),
          },
        }),
        ...(sourceAttrs.statusAndCitizenship != null && {
          statusAndCitizenship: {
            ...sourceAttrs.statusAndCitizenship,
            ...(strippedPassports != null && { passports: strippedPassports }),
          },
        }),
      }

      await updateApplicationAsync({ attributes: attributesToCopy })
      await copyDocumentsAsync({ sourceApplicationId: sourceId })
      await queryClient.refetchQueries({ queryKey: ['applications', currentApplicationId] })
    } catch (err) {
      if (err instanceof Error) {
        toast.negative(new Error(err.message))
      }
    } finally {
      setIsPrefilling(false)
      setShowPrefillDialog(false)
      setCurrentStep(ApplicationSteps.PERSONAL_INFORMATION)
    }
  }

  const handleStartFreshApplication = () => {
    setCurrentStep(ApplicationSteps.PERSONAL_INFORMATION)
  }

  const handleSubmitApplication = (
    successCallback: (response: RawApplicationResponse) => void,
    errorCallback: (err: GenericError) => void,
  ) => {
    submitApplication(undefined, {
      onSuccess: response => {
        successCallback(response)
      },
      onError: err => {
        if (err instanceof Error) {
          errorCallback(err)
        }
      },
    })
  }

  const handleSubmitConfirmation = async () => {
    handleSubmitApplication(
      (response: RawApplicationResponse) => {
        queryClient.setQueryData(
          ['applications', response.data.id],
          (oldData: RawApplicationAggregationResponse): RawApplicationAggregationResponse => {
            return {
              data: {
                ...oldData.data,
                attributes: {
                  ...oldData.data.attributes,
                  application: response.data,
                },
              },
            }
          },
        )

        setShowSubmitApprovalDialog(false)
        setShowSubmitConfirmation(true)
        setSearchParams({ applicationId: currentApplicationId, submitted: 'true' })
      },
      (err: GenericError) => {
        setShowSubmitApprovalDialog(false)
        setShowSubmitConfirmation(false)
        toast.negative(err)
      },
    )
  }

  const handleSubmitApplicationTrigger = async () => {
    const formId = timelineItems.at(-1)?.formId
    if (formId) {
      try {
        const formElement = document.getElementById(formId) as HTMLFormElement
        if (formElement) {
          formElement.requestSubmit()

          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['program-intake', intake?.id] }),
            queryClient.invalidateQueries({ queryKey: ['applications', application?.id] }),
            queryClient.invalidateQueries({ queryKey: ['application-page-props'] }),
          ])

          const [updatedApplication, updatedIntake, updatedApplicationPageProps] = [
            queryClient.getQueryData([
              'applications',
              application?.id,
            ]) as RawApplicationAggregationResponse,
            queryClient.getQueryData(['program-intake', intake?.id]) as GetProgramIntakeResponse,
            queryClient.getQueryData(['application-page-props']) as GetApplicationPagePropsResponse,
          ]

          if (
            updatedApplication?.data?.attributes.application.attributes?.applicationState !==
            ApplicationState.DRAFT
          ) {
            toast.negative(
              new Error(
                'Payment in progress: Another payment is already being processed for this application',
              ),
            )
            navigate(`/applications/${updatedApplication.data.id}`)
            return
          }

          const { isRequiredSpecificFieldsValid, requiredSpecificFieldsErrors } =
            validateRequiredSpecificFields(
              updatedApplicationPageProps?.data?.specificFields,
              student,
              updatedApplication?.data?.attributes?.application,
            )

          if (updatedIntake?.data?.attributes?.isAvailable === false) {
            setOpenIntakeClosedDialog(true)
          } else if (!canSubmitApplication) {
            setShowPolicyBlockedDialog(true)
          } else if (isRequiredSpecificFieldsValid) {
            setShowSubmitApprovalDialog(true)
          } else {
            setSpecificFieldsValidationErrors(requiredSpecificFieldsErrors)
            setShowSpecificFieldsValidationErrorsDialog(true)
          }
        } else {
          throw new Error('Failed to submit application. Please try again.')
        }
      } catch (error) {
        toast.negative(error as GenericError)
      }
    }
  }

  if ((currentApplicationId && isLoadingApplication) || isLoadingApplicationPageProps) {
    return (
      <NavBars>
        <Loading />
      </NavBars>
    )
  }

  const prefillDialog = previousSubmittedApplication ? (
    <PrefillApplicationDialog
      open={showPrefillDialog}
      onOpenChange={setShowPrefillDialog}
      previousApplication={{
        programName: previousSubmittedApplication.attributes?.programSelected?.program?.name ?? '',
        intakeTermName:
          previousSubmittedApplication.attributes?.programSelected?.programIntakeTerm?.name ?? '',
        submittedDate:
          previousSubmittedApplication.attributes?.applicationInformation?.submittedAt ?? '',
        humanReadableId: previousSubmittedApplication.attributes?.humanReadableId ?? '',
      }}
      onPrefill={handlePrefillApplication}
      onStartFresh={handleStartFreshApplication}
      isLoading={isPrefilling}
    />
  ) : null

  const onPreviousTabButton = () => {
    queryClient.invalidateQueries({
      queryKey: ['applications', application?.id],
    })
    setCurrentStep(selectedTab)
    window.scroll({
      top: 0,
    })
  }

  if (
    !application ||
    (currentStep === ApplicationSteps.PROGRAM_SELECT && isEditingProgramRef.current)
  ) {
    return (
      <>
        <SelectProgram
          application={application}
          isCreatingApplication={isCreatingApplication}
          isUpdatingApplication={isUpdatingApplication}
          setCurrentStep={setCurrentStep}
          handleCreateApplication={handleCreateApplication}
          handleUpdateApplication={handleUpdateApplication}
          currentApplicationId={currentApplicationId}
        />
        {prefillDialog}
      </>
    )
  }

  if (
    !skipAlternateProgram &&
    application &&
    (currentStep === ApplicationSteps.ALTERNATE_PROGRAM_SELECT ||
      !application?.attributes?.alternateProgramSelected)
  ) {
    return (
      <>
        <SelectAlternateProgram
          application={application}
          isUpdatingApplication={isUpdatingApplication}
          setCurrentStep={setCurrentStep}
          handleUpdateApplication={handleUpdateApplication}
          currentApplicationId={currentApplicationId}
          onBackToProgramSelection={() => {
            isEditingProgramRef.current = true
            setCurrentStep(ApplicationSteps.PROGRAM_SELECT)
          }}
        />
        {prefillDialog}
      </>
    )
  }

  if (currentStep < ApplicationSteps.PERSONAL_INFORMATION + 1) {
    selectedTab =
      timelineItems.find(item => !item.completed)?.step ?? ApplicationSteps.PERSONAL_INFORMATION //return the first incomplete tab
  }

  return (
    <>
      <NavBars largePage>
        <CreateApplicationErrorBoundary>
          <BackToApplication />
          {policyEvaluation && !canSubmitApplication ? (
            <PolicyBanner
              evaluation={policyEvaluation}
              intakeTerm={application?.attributes?.programSelected?.programIntakeTerm?.name}
            />
          ) : null}
          <Flex direction="column" gap={10}>
            <Flex hideAbove="md" pb={3}>
              <ProgressionWrapper>
                <ProgressIndicator
                  variant="linear"
                  label="Completion"
                  size="lg"
                  total={100}
                  value={(selectedTab - 1) * (100 / lastTimeline)}
                />
              </ProgressionWrapper>
            </Flex>

            <Flex wrap={false} align="center" justify="between" gap={4}>
              <Flex
                hideBelow={
                  selectedTab > lastTimeline && showSubmitApprovalDialog ? 'sm' : undefined
                }
                align={{ xs: 'start', md: 'center' }}
                gap={4}
                direction={{ xs: 'column', md: 'row' }}
              >
                <Heading level={1} variant="headlineL">
                  New Application
                </Heading>
                {application ? (
                  <ApplicationStateTag
                    state={application.attributes?.applicationState}
                    hasOpenDocumentRequests={application.attributes?.hasOpenDocumentRequests}
                    statusInfo={statusInfo}
                  />
                ) : null}
              </Flex>
              <DropdownMenu
                application={application}
                alternateProgramEnabled={alternateProgramEnabled}
                onEditProgram={() => {
                  isEditingProgramRef.current = true
                  setCurrentStep(ApplicationSteps.PROGRAM_SELECT)
                }}
                onEditAlternateProgram={() => {
                  isEditingProgramRef.current = true
                  setCurrentStep(ApplicationSteps.ALTERNATE_PROGRAM_SELECT)
                }}
              />
            </Flex>
            <GridAlpha
              columnGap={12}
              rowGap={5}
              columns={{
                xs: 'minmax(0, 1fr)',
                md: '336px minmax(0, 1fr)',
              }}
              areas={{
                xs: ['aside', 'form'],
                md: ['aside form'],
              }}
            >
              <GridAlpha.Item areaName="aside">
                <Flex direction="column" gap={4}>
                  <Flex
                    hideBelow={
                      selectedTab > lastTimeline && showSubmitApprovalDialog ? 'sm' : undefined
                    }
                    direction="column"
                  >
                    <ApplicationHeader
                      application={application}
                      alternateProgramEnabled={alternateProgramEnabled}
                    />
                  </Flex>
                  <ApplicationTimeline
                    selectedTab={selectedTab}
                    setSelectedTab={tab => {
                      setOpenIntakeClosedDialog(intake?.attributes?.isAvailable === false)
                      setCurrentStep(tab + 1)
                    }}
                    application={application}
                  />
                </Flex>
              </GridAlpha.Item>
              <GridAlpha.Item areaName="form">
                <Flex
                  grow={1}
                  hideBelow={showSubmitApprovalDialog || showSubmitConfirmation ? 'sm' : undefined}
                >
                  <ApplicationForms
                    selectedTab={selectedTab}
                    application={application}
                    onPreviousTabButton={onPreviousTabButton}
                    focusRef={paySubmitButtonRef}
                    onSuccess={response => {
                      if (response && selectedTab <= lastTimeline) {
                        queryClient.invalidateQueries({
                          queryKey: ['applications', response.data.id],
                        })
                        setCurrentStep(selectedTab + 2)
                      }
                    }}
                    onError={err => {
                      if (err.message.trim() !== '') {
                        toast.negative(err)
                      }
                      queryClient.invalidateQueries({ queryKey: ['applications', application.id] })
                    }}
                    submitApplication={handleSubmitApplicationTrigger}
                  />
                </Flex>
                {hasApplicationFee(application) ? (
                  <PayAndSubmitApprovalDialog
                    showSubmitApprovalDialog={showSubmitApprovalDialog}
                    setShowSubmitApprovalDialog={setShowSubmitApprovalDialog}
                    application={application}
                  />
                ) : (
                  <SubmitApprovalDialog
                    showSubmitApprovalDialog={showSubmitApprovalDialog}
                    setShowSubmitApprovalDialog={setShowSubmitApprovalDialog}
                    onSubmitConfirmation={handleSubmitConfirmation}
                    isLoading={isSubmittingApplication}
                  />
                )}

                <SubmitConfirmation
                  showSubmitConfirmation={showSubmitConfirmation}
                  setShowSubmitConfirmation={setShowSubmitConfirmation}
                  onClose={() => {
                    setSearchParams({ applicationId: currentApplicationId })
                    navigate(`/applications/${application?.id}`)
                  }}
                />
              </GridAlpha.Item>
            </GridAlpha>
          </Flex>
        </CreateApplicationErrorBoundary>
      </NavBars>

      <IntakeClosedDialog
        open={openIntakeClosedDialog}
        onOpenChange={setOpenIntakeClosedDialog}
        onEditProgram={() => {
          isEditingProgramRef.current = true
          setCurrentStep(ApplicationSteps.PROGRAM_SELECT)
        }}
      />

      <SpecificFieldsValidationErrorsDialog
        open={showSpecificFieldsValidationErrorsDialog}
        onOpenChange={setShowSpecificFieldsValidationErrorsDialog}
        errors={specificFieldsValidationErrors}
      />

      <PolicyBlockedDialog
        showPolicyBlockedDialog={showPolicyBlockedDialog}
        setShowPolicyBlockedDialog={setShowPolicyBlockedDialog}
        intakeTerm={application?.attributes?.programSelected?.programIntakeTerm?.name}
        onEditProgram={() => {
          isEditingProgramRef.current = true
          setCurrentStep(ApplicationSteps.PROGRAM_SELECT)
        }}
      />
    </>
  )
}

const ProgressionWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  '> div': {
    width: '100%',
    '> div:first-of-type': {
      display: 'none',
    },
  },
})

function getInitialStep(
  application: StudentApplication | null,
  timelineItems: TimelineObject[],
  isEditingProgramRef: boolean,
  skipAlternateProgram: boolean,
) {
  if (isEditingProgramRef) {
    return ApplicationSteps.PROGRAM_SELECT
  }

  const firstCompletedFormStep = timelineItems.find(item => item.completed)
  if (firstCompletedFormStep) {
    return firstCompletedFormStep.step
  }

  if (application) {
    if (
      application.attributes?.alternateProgramSelected &&
      application.attributes.programSelected
    ) {
      return ApplicationSteps.PERSONAL_INFORMATION
    }

    if (
      !application.attributes?.alternateProgramSelected &&
      application.attributes?.programSelected
    ) {
      return skipAlternateProgram
        ? ApplicationSteps.PERSONAL_INFORMATION
        : ApplicationSteps.ALTERNATE_PROGRAM_SELECT
    }
  }

  return ApplicationSteps.PROGRAM_SELECT
}
